import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import {
  FileText, Search, Copy, Download, ArrowRight, Loader2, AlertTriangle,
  Sparkles, Users2, Clock, Video, Disc3, X,
} from "lucide-react";

const SPEAKER_STYLES = [
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-orange-100 text-orange-800 border-orange-200",
  "bg-violet-100 text-violet-800 border-violet-200",
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-rose-100 text-rose-800 border-rose-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
];

function speakerName(label, smap) {
  const m = (smap || {})[label];
  if (m && m.name) return m.name;
  if (label && label.length <= 2) return `Speaker ${label}`;
  return label || "Speaker";
}

function transcriptToText(rec) {
  const utts = rec?.utterances || [];
  const smap = rec?.speaker_map || {};
  if (utts.length) return utts.map((u) => `${speakerName(u.speaker, smap)}: ${u.text}`).join("\n\n");
  return rec?.transcript_text || "";
}

function fmtDuration(s) {
  s = Math.max(0, Math.round(Number(s) || 0));
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = (m % 60).toString().padStart(2, "0");
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

function sanitizeFilename(name) {
  return (name || "transcript").replace(/[^\w\d\-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "") || "transcript";
}

function downloadTxt(rec) {
  const text = transcriptToText(rec);
  if (!text.trim()) { toast.error("No transcript text to download"); return; }
  const header = `${rec.title}\n${rec.transcription_provider ? `Source: ${rec.transcription_provider}\n` : ""}${"=".repeat(40)}\n\n`;
  const blob = new Blob([header + text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitizeFilename(rec.title)}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success("Transcript downloaded");
}

async function copyTranscript(rec) {
  const text = transcriptToText(rec);
  if (!text.trim()) { toast.error("No transcript text to copy"); return; }
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Transcript copied to clipboard");
  } catch (e) {
    toast.error("Could not copy — try the download instead");
  }
}

function ProviderBadge({ r }) {
  if (r.is_sandbox) return <Badge className="border border-amber-200 bg-amber-50 text-amber-800">Sandbox</Badge>;
  const p = r.transcription_provider;
  if (!p) return null;
  const label = p === "manual" ? "Pasted" : (p.toLowerCase().includes("assembly") ? "AssemblyAI" : p);
  return <Badge variant="secondary" data-testid="tx-provider-badge">{label}</Badge>;
}

function ReadTranscriptDialog({ id, open, onOpenChange }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !id) { setRec(null); return; }
    setLoading(true);
    api.get(`/recordings/${id}`)
      .then((r) => setRec(r.data))
      .catch(() => toast.error("Could not load transcript"))
      .finally(() => setLoading(false));
  }, [open, id]);

  const utts = useMemo(() => rec?.utterances || [], [rec]);
  const labels = useMemo(() => [...new Set(utts.map((u) => u.speaker))], [utts]);
  const labelColor = (label) => SPEAKER_STYLES[Math.max(0, labels.indexOf(label)) % SPEAKER_STYLES.length];
  const smap = rec?.speaker_map || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] flex-col gap-0 p-0 sm:max-w-2xl" data-testid="tx-read-dialog">
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="flex items-center gap-2 pr-6 font-serif text-lg">
            <FileText className="h-5 w-5 shrink-0 text-primary" />
            <span className="line-clamp-1">{rec?.title || "Transcript"}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Full transcript with speaker labels. Copy or download the text below.</DialogDescription>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {rec && <ProviderBadge r={rec} />}
            {rec?.has_video && <Badge variant="secondary"><Video className="mr-1 h-3 w-3" />Video</Badge>}
            {labels.length > 0 && <span className="text-xs text-muted-foreground"><Users2 className="mr-1 inline h-3.5 w-3.5" />{labels.length} speaker{labels.length === 1 ? "" : "s"}</span>}
          </div>
        </DialogHeader>

        <ScrollArea className="min-h-0 flex-1 px-5 py-4" data-testid="tx-read-body">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading transcript…</div>
          ) : utts.length > 0 ? (
            <div className="space-y-3 pr-3">
              {utts.map((u, i) => (
                <div key={u.start != null ? `${u.start}-${u.speaker ?? ""}` : i} className="flex gap-3">
                  <Badge variant="outline" className={`mt-0.5 h-fit shrink-0 ${labelColor(u.speaker)}`}>{speakerName(u.speaker, smap)}</Badge>
                  <p className="text-[15px] leading-7 text-foreground">{u.text}</p>
                </div>
              ))}
            </div>
          ) : rec?.transcript_text ? (
            <p className="whitespace-pre-wrap pr-3 text-[15px] leading-7 text-foreground">{rec.transcript_text}</p>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">No transcript text available yet.</p>
          )}
        </ScrollArea>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={() => copyTranscript(rec)} disabled={!rec} data-testid="tx-dialog-copy"><Copy className="mr-2 h-4 w-4" />Copy</Button>
          <Button variant="secondary" size="sm" onClick={() => downloadTxt(rec)} disabled={!rec} data-testid="tx-dialog-download"><Download className="mr-2 h-4 w-4" />Download .txt</Button>
          {rec && (
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="tx-dialog-open-studio">
              <Link to={`/admin/recordings/${rec.recording_id}`}><Sparkles className="mr-2 h-4 w-4" />Open in Studio</Link>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TranscriptRow({ r, onRead }) {
  const transcribing = r.status === "transcribing";
  const failed = r.status === "transcribe_failed";

  return (
    <Card className="border-border bg-card transition-shadow hover:shadow-[0_12px_30px_rgba(20,20,20,0.08)]" data-testid="tx-row">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="line-clamp-1 font-serif text-lg font-semibold text-foreground" data-testid="tx-row-title">{r.title}</h3>
            <ProviderBadge r={r} />
            {r.has_video && <Badge variant="secondary"><Video className="mr-1 h-3 w-3" />Video</Badge>}
            {r.has_analysis && <Badge className="border border-primary/30 bg-primary/10 text-primary"><Sparkles className="mr-1 h-3 w-3" />AI insights</Badge>}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>{formatDate(r.created_at)}</span>
            {r.duration_seconds > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{fmtDuration(r.duration_seconds)}</span>}
            {r.word_count > 0 && <span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{r.word_count.toLocaleString()} words</span>}
            {r.speaker_count > 0 && <span className="inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5" />{r.speaker_count} speaker{r.speaker_count === 1 ? "" : "s"}</span>}
          </div>
          {transcribing ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-primary" data-testid="tx-row-processing"><Loader2 className="h-4 w-4 animate-spin" />Transcribing with AssemblyAI…</p>
          ) : failed ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />Transcription failed{r.transcribe_error ? `: ${r.transcribe_error}` : "."}</p>
          ) : r.transcript_preview ? (
            <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{r.transcript_preview}…</p>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {transcribing ? (
            <Button asChild size="sm" variant="secondary" data-testid="tx-row-open"><Link to={`/admin/recordings/${r.recording_id}`}>Open <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
          ) : failed ? (
            <Button asChild size="sm" variant="secondary" data-testid="tx-row-retry"><Link to={`/admin/recordings/${r.recording_id}`}>Open to retry <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
          ) : (
            <>
              <Button size="sm" onClick={() => onRead(r.recording_id)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="tx-row-read"><FileText className="mr-1.5 h-4 w-4" />Read</Button>
              <Button asChild size="sm" variant="ghost" data-testid="tx-row-open"><Link to={`/admin/recordings/${r.recording_id}`}>Studio <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link></Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminTranscripts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [readId, setReadId] = useState(null);
  const [readOpen, setReadOpen] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/recordings");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (!silent) toast.error("Could not load transcripts");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only transcripts (ready, in-progress, or failed) belong on this page.
  const transcripts = useMemo(
    () => items.filter((r) => r.has_transcript || r.status === "transcribing" || r.status === "transcribe_failed"),
    [items],
  );

  const processingCount = useMemo(() => transcripts.filter((r) => r.status === "transcribing").length, [transcripts]);

  // Live-refresh while any transcript is still processing.
  useEffect(() => {
    if (processingCount > 0 && !pollRef.current) {
      pollRef.current = setInterval(() => load(true), 5000);
    } else if (processingCount === 0 && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [processingCount, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return transcripts;
    return transcripts.filter((r) => (r.title || "").toLowerCase().includes(q) || (r.transcript_preview || "").toLowerCase().includes(q));
  }, [transcripts, query]);

  const openRead = (id) => { setReadId(id); setReadOpen(true); };

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-5" data-testid="admin-transcripts">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Transcripts</h1>
          <p className="mt-1 text-muted-foreground">Every recording's AssemblyAI transcript in one place — read, copy or download.</p>
        </div>
        <Button asChild className="bg-red-600 text-white hover:bg-red-700" data-testid="tx-record-button">
          <Link to="/studio"><Disc3 className="mr-2 h-4 w-4" />Record session</Link>
        </Button>
      </div>

      {transcripts.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search transcripts by title or text…"
            className="pl-9 pr-9"
            data-testid="tx-search-input"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {processingCount > 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="tx-processing-banner"><CardContent className="flex items-center gap-3 p-3.5">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
          <p className="text-sm text-foreground">{processingCount} transcript{processingCount === 1 ? "" : "s"} still processing — this page updates automatically.</p>
        </CardContent></Card>
      )}

      {transcripts.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No transcripts yet"
          description="Record a session in the Studio (or upload audio / paste a transcript). Recordings are automatically transcribed with AssemblyAI and will show up here."
          testId="tx-empty"
          action={<Button asChild className="bg-red-600 text-white hover:bg-red-700"><Link to="/studio"><Disc3 className="mr-2 h-4 w-4" />Record session</Link></Button>}
        />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Search} title="No matches" description={`No transcripts match "${query}".`} testId="tx-no-match" />
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <TranscriptRow key={r.recording_id} r={r} onRead={openRead} />
          ))}
        </div>
      )}

      <ReadTranscriptDialog id={readId} open={readOpen} onOpenChange={setReadOpen} />
    </div>
  );
}
